import { useState, useCallback, useRef } from 'react';

interface UseFileUploadReturn {
  selectedFiles: File[];
  firstTwoLines: { [key: string]: string };
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  resetFiles: () => void;
  removeFile: (index: number) => void;
  file: File | null;
  fileContent: string | null;
  delimiter: string;
  hasHeader: boolean;
  dateFormat: string;
  customDateFormat: string;
  setFile: (file: File | null) => void;
  setFileContent: (content: string | null) => void;
  setDelimiter: (delimiter: string) => void;
  setHasHeader: (hasHeader: boolean) => void;
  setDateFormat: (format: string) => void;
  setCustomDateFormat: (format: string) => void;
  reset: () => void;
}

export const useFileUpload = (): UseFileUploadReturn => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [firstTwoLines, setFirstTwoLines] = useState<{ [key: string]: string }>({});
  const [file, setFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [delimiter, setDelimiter] = useState<string>(',');
  const [hasHeader, setHasHeader] = useState<boolean>(true);
  const [dateFormat, setDateFormat] = useState<string>('ISO');
  const [customDateFormat, setCustomDateFormat] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    // Handle case when all files are removed
    if (!files || files.length === 0) {
      setSelectedFiles([]);
      setFirstTwoLines({});
      setFile(null);
      return;
    }

    const filesArray = Array.from(files);

    setSelectedFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));

      // Detect if files were removed vs added:
      // - If ALL files in filesArray already exist in prev → files were removed (removeFile called)
      // - If ANY file in filesArray is new → files were added (user selected new files)
      const allFilesExist = filesArray.every(f => existingNames.has(f.name));
      const filesWereRemoved = allFilesExist && filesArray.length < prev.length;

      let allFiles: File[];
      if (filesWereRemoved) {
        // Files were removed - use the new list as-is (replace)
        allFiles = filesArray;
      } else {
        // Files were added - accumulate and prevent duplicates
        const newFiles = filesArray.filter(f => !existingNames.has(f.name));
        allFiles = [...prev, ...newFiles];
      }

      // Clear and rebuild firstTwoLines for ALL current files
      setFirstTwoLines({});

      // Read first two lines from each file
      allFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          const lines = content.split('\n');
          const firstTwo = lines.slice(0, 2).join('\n');
          setFirstTwoLines(prev => ({ ...prev, [file.name]: firstTwo }));
        };
        reader.readAsText(file.slice(0, 1024)); // Read only first 1KB
      });

      return allFiles;
    });

    setFile(filesArray[0]);
  }, []);

  const resetFiles = useCallback(() => {
    setSelectedFiles([]);
    setFirstTwoLines({});
    setFile(null);
    setFileContent(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const removeFile = useCallback((index: number) => {
    setSelectedFiles(prev => {
      const newFiles = prev.filter((_, i) => i !== index);
      
      // Update firstTwoLines to remove the deleted file
      const removedFile = prev[index];
      if (removedFile) {
        setFirstTwoLines(prevLines => {
          const newLines = { ...prevLines };
          delete newLines[removedFile.name];
          return newLines;
        });
      }
      
      // Update file input to reflect the change
      if (fileInputRef.current && newFiles.length > 0) {
        const dataTransfer = new DataTransfer();
        newFiles.forEach(f => dataTransfer.items.add(f));
        fileInputRef.current.files = dataTransfer.files;
      } else if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Update main file reference
      if (newFiles.length > 0) {
        setFile(newFiles[0]);
      } else {
        setFile(null);
        setFileContent(null);
      }
      
      return newFiles;
    });
  }, []);

  const reset = useCallback(() => {
    resetFiles();
    setDelimiter(',');
    setHasHeader(true);
    setDateFormat('ISO');
    setCustomDateFormat('');
  }, [resetFiles]);

  return {
    selectedFiles,
    firstTwoLines,
    fileInputRef,
    handleFileChange,
    resetFiles,
    removeFile,
    file,
    fileContent,
    delimiter,
    hasHeader,
    dateFormat,
    customDateFormat,
    setFile,
    setFileContent,
    setDelimiter,
    setHasHeader,
    setDateFormat,
    setCustomDateFormat,
    reset
  };
};